<template>
  <div>
    <c-title>{{ tutorType }} vorschlagen</c-title>

    <c-busy v-if="loadStatus === $config.status.LOADING" />

    <c-form
      v-else
      required-fields
      @submit.prevent="submitSuggest"
    >

      <c-grid>

        <c-form-select
          id="institution"
          :errors="[{
            message: 'Dieses Feld ist erforderlich.',
            show: !$v.institution.required
          }]"
          :has-error="$v.institution.$error"
          :is-valid="!$v.institution.$invalid"
          :label="`${institutionType} *`"
          v-model="institution"
          aria-required="true"
          @input="$v.institution.$touch"
        >

          <option
            disabled
            selected
            value=""
          >
            Bitte wählen
          </option>

          <option
            v-for="option in options"
            v-if="option.type === type"
            :key="option.identifier"
            :value="option.identifier"
          >
            {{ option.name }}
          </option>

        </c-form-select>

        <c-grid-item :span="2">
          <c-form-input
            id="title"
            v-model="title"
            autocomplete="no"
            label="Titel"
          />
        </c-grid-item>

        <c-grid-item :span="5">
          <c-form-input
            id="first-name"
            :errors="[{
              message: 'Dieses Feld ist erforderlich.',
              show: !$v.firstName.required
            }]"
            :has-error="$v.firstName.$error"
            :is-valid="!$v.firstName.$invalid"
            v-model="firstName"
            aria-required="true"
            autocomplete="no"
            label="Vorname *"
            @input="$v.firstName.$touch"
          />
        </c-grid-item>

        <c-grid-item :span="5">
          <c-form-input
            id="last-name"
            :errors="[{
              message: 'Dieses Feld ist erforderlich.',
              show: !$v.lastName.required
            }]"
            :has-error="$v.lastName.$error"
            :is-valid="!$v.lastName.$invalid"
            v-model="lastName"
            aria-required="true"
            autocomplete="no"
            label="Nachname *"
            @input="$v.lastName.$touch"
          />
        </c-grid-item>

      </c-grid>

      <c-form-input
        v-if="type === $config.project.type.UNIVERSITY"
        id="faculty"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.faculty.required
        }]"
        :has-error="$v.faculty.$error"
        :is-valid="!$v.faculty.$invalid"
        v-model="faculty"
        aria-required="true"
        label="Fachbereich *"
        @input="$v.faculty.$touch"
      />

      <c-form-textarea
        id="comment"
        :errors="[{
          message: 'Dieses Feld ist erforderlich.',
          show: !$v.comment.required
        }]"
        :has-error="$v.comment.$error"
        :is-valid="!$v.comment.$invalid"
        :label="`Kommentar (max. ${$v.comment.$params.maxLength.max} Zeichen) *`"
        v-model="comment"
        aria-required="true"
        rows="10"
        @input="$v.comment.$touch"
      />

      <c-button :busy="saveStatus === $config.status.SAVING">
        Vorschlag einreichen
      </c-button>

    </c-form>

  </div>
</template>

<script>
import CBusy from '@/components/CBusy'
import CButton from '@/components/CButton'
import CForm from '@/components/form/CForm'
import CFormFieldset from '@/components/form/CFormFieldset'
import CFormInput from '@/components/form/CFormInput'
import CFormSelect from '@/components/form/CFormSelect'
import CFormTextarea from '@/components/form/CFormTextarea'
import CGrid from '@/components/CGrid'
import CGridItem from '@/components/CGridItem'
import CTitle from '@/components/CTitle'

import config from '@/config'
import handleRequestError from '@/functions/handleRequestError'
import { maxLength, required } from 'vuelidate/lib/validators'

export default {
  name: 'VProjectSuggest',

  components: {
    CBusy,
    CButton,
    CForm,
    CFormFieldset,
    CFormInput,
    CFormSelect,
    CFormTextarea,
    CGrid,
    CGridItem,
    CTitle
  },

  props: {
    type: {
      type: Number,
      required: true,
      validator (value) {
        return Object.values(config.project.type).indexOf(value) >= 0
      }
    }
  },

  data () {
    return {
      loadStatus: this.$config.status.IDLE,
      saveStatus: this.$config.status.IDLE,
      options: [],
      institution: null,
      title: '',
      firstName: '',
      lastName: '',
      faculty: '',
      comment: ''
    }
  },

  computed: {
    tutorType () {
      return this.type === this.$config.project.type.SCHOOL
        ? 'Lehrer/-in'
        : 'Professor/-in'
    },
    institutionType () {
      return this.type === this.$config.project.type.SCHOOL
        ? 'Schule'
        : 'Hochschule'
    }
  },

  validations () {
    const schema = {
      institution: {
        required
      },
      firstName: {
        required
      },
      lastName: {
        required
      },
      comment: {
        maxLength: maxLength(1000),
        required
      }
    }

    if (this.type === this.$config.project.type.UNIVERSITY) {
      schema.faculty = { required }
    }

    return schema
  },

  created () {
    this.loadStatus = this.$config.status.LOADING

    this.$api.get('projects/school/')
      .then(response => {
        this.options = response.data
        this.loadStatus = this.$config.status.SUCCESS
      })
      .catch(error => {
        handleRequestError({
          error,
          fallback: 'Das Vorschlagsformular kann zurzeit leider nicht geladen werden. Bitte versuchen Sie es später erneut.'
        })

        this.$router.push({ name: 'dashboard' })
      })
  },

  methods: {
    submitSuggest () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$store.commit('notifications/add', {
          text: 'Bitte füllen Sie alle Felder korrekt aus.',
          type: 'error'
        })

        return
      }

      this.saveStatus = this.$config.status.SAVING

      this.$api.post('projects/suggest/', {
        school: this.institution,
        first_name: this.firstName,
        last_name: this.lastName,
        comment: this.comment,
        faculty: this.faculty,
        title: this.title
      })
        .then(response => {
          this.$store.commit('notifications/add', {
            text: 'Vielen Dank für Ihren Vorschlag.',
            type: 'success'
          })
          this.$router.push({ name: 'dashboard' })
        })
        .catch(error => {
          handleRequestError({
            error,
            fallback: 'Ihr Vorschlag kann zurzeit leider nicht geladen werden. Bitte versuchen Sie es später erneut.'
          })

          this.saveStatus = this.$config.status.ERROR
        })
    }
  }
}
</script>
